import { FC } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import intl from 'components/i18n/ReactIntlWrapper';

interface CorporateEntityOption {
  id: number;
  label: string;
  name: string;
}

interface CorporateEntityAutocompleteProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  label?: string;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  disabled?: boolean;
}

const CorporateEntityAutocomplete: FC<CorporateEntityAutocompleteProps> = ({
  value,
  onChange,
  label = intl.get('accountManagement.corporateEntity'),
  helperText,
  error = false,
  required = false,
  disabled = false,
}) => {
  const { invoiceApi }: Api = useApi();

  // Fetch all corporate entities
  const { data: corporateEntities, isLoading } = useQuery({
    queryKey: ['corporateEntities'],
    queryFn: async (): Promise<CorporateEntityOption[]> => {
      const response = await invoiceApi.apiVversionCorporateEntitySearchGet('1', '');
      const entities = response.data.data?.queryResult || [];
      return entities.map((entity: any) => ({
        id: entity.id,
        label: entity.name || '',
        name: entity.name || '',
      }));
    },
    staleTime: 300000, // 5 minutes
  });

  // Find selected option
  const selectedOption = corporateEntities?.find(opt => opt.id === value) || null;

  return (
    <Autocomplete
      value={selectedOption}
      onChange={(_, newValue) => {
        onChange(newValue?.id);
      }}
      options={corporateEntities || []}
      getOptionLabel={(option) => option.label}
      loading={isLoading}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default CorporateEntityAutocomplete;
